import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { DifficultySelector, GameOverScreen } from "./GameShell";
import { toast } from "sonner";

const DIFFICULTY_CONFIG = {
  easy:   { startPairs: 4, maxLevel: 4, peekTime: 1500, bonusMult: 1 },
  medium: { startPairs: 4, maxLevel: 5, peekTime: 1000, bonusMult: 1.5 },
  hard:   { startPairs: 6, maxLevel: 5, peekTime: 600,  bonusMult: 2 },
};

const SYMBOLS = ["A","B","C","D","E","F","G","H","I","J","K","L"];
const COLORS = [
  "bg-red-400","bg-blue-400","bg-green-400","bg-yellow-400",
  "bg-purple-400","bg-pink-400","bg-cyan-400","bg-orange-400",
  "bg-teal-400","bg-indigo-400","bg-rose-400","bg-amber-400"
];

const TUTORIAL_STEPS = [
  { visual: "🃏", title: "Flip Cards", description: "Tap cards to flip them over and reveal the symbol underneath." },
  { visual: "🔍", title: "Find Pairs", description: "Match two cards with the same symbol. Matched pairs stay revealed." },
  { visual: "🏆", title: "Clear the Board", description: "Find all pairs to advance. Fewer moves = higher bonus score!" },
];

const PatternMatchGame = ({ onComplete, onBack, t }) => {
  const [difficulty, setDifficulty] = useState(null);
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [canFlip, setCanFlip] = useState(true);
  const [config, setConfig] = useState(null);
  const [peeking, setPeeking] = useState(false);

  const initLevel = useCallback((lvl, cfg) => {
    const pairCount = Math.min(cfg.startPairs + lvl - 1, 12);
    const selected = SYMBOLS.slice(0, pairCount);
    const deck = [...selected, ...selected]
      .map((sym, i) => ({ id: i, symbol: sym, color: COLORS[selected.indexOf(sym)] }))
      .sort(() => Math.random() - 0.5);
    setCards(deck);
    setFlipped([]);
    setMatched([]);
    setCanFlip(false);

    // Brief peek at all cards
    setPeeking(true);
    setTimeout(() => {
      setPeeking(false);
      setCanFlip(true);
    }, cfg.peekTime);
  }, []);

  const startGame = useCallback((diff) => {
    const cfg = DIFFICULTY_CONFIG[diff];
    setDifficulty(diff);
    setConfig(cfg);
    setLevel(1);
    setScore(0);
    setMoves(0);
    setGameOver(false);
    initLevel(1, cfg);
  }, [initLevel]);

  const handleCardClick = (idx) => {
    if (!canFlip || flipped.includes(idx) || matched.includes(idx) || peeking) return;

    const newFlipped = [...flipped, idx];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setCanFlip(false);
      setMoves((m) => m + 1);
      const [first, second] = newFlipped;

      if (cards[first].symbol === cards[second].symbol) {
        const newMatched = [...matched, first, second];
        setMatched(newMatched);
        setScore((s) => s + Math.round(20 * config.bonusMult));
        setFlipped([]);
        setCanFlip(true);

        if (newMatched.length === cards.length) {
          const bonus = Math.max(0, Math.round((100 - moves * 2) * config.bonusMult));
          setScore((s) => s + bonus);
          if (level < config.maxLevel) {
            setLevel((l) => l + 1);
            toast.success(t("games.levelComplete", { level }));
            setTimeout(() => initLevel(level + 1, config), 600);
          } else {
            setGameOver(true);
            const finalScore = score + Math.round(20 * config.bonusMult) + bonus;
            const accuracy = Math.round((matched.length + 2) / ((moves + 1) * 2) * 100);
            onComplete(finalScore, level, accuracy, difficulty);
          }
        }
      } else {
        setTimeout(() => { setFlipped([]); setCanFlip(true); }, 600);
      }
    }
  };

  if (!difficulty) {
    return (
      <DifficultySelector
        gameName={t("games.patternMatch.name") || "Classic Memory"}
        gameDescription={t("games.patternMatch.desc") || "Find matching pairs of cards"}
        tutorialSteps={TUTORIAL_STEPS}
        onStart={startGame}
        onBack={onBack}
        t={t}
      />
    );
  }

  if (gameOver) {
    return (
      <GameOverScreen
        score={score}
        subtitle={`${moves} ${t("games.patternMatch.moves")} | ${t("games.difficulty." + difficulty)}`}
        onPlayAgain={() => startGame(difficulty)}
        onBack={onBack}
        t={t}
      />
    );
  }

  const gridCols = cards.length <= 8 ? 4 : cards.length <= 16 ? 4 : 6;

  return (
    <div className="text-center" data-testid="pattern-game-active">
      <div className="flex justify-between mb-6">
        <div>
          <p className="text-sm text-slate-500">{t("common.level")}</p>
          <p className="text-xl font-medium text-sky-800">{level}/{config?.maxLevel}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">{t("common.score")}</p>
          <p className="text-xl font-medium text-sky-800">{score}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">{t("games.patternMatch.moves")}</p>
          <p className="text-xl font-medium text-sky-800">{moves}</p>
        </div>
      </div>

      <div
        className="grid gap-3 max-w-md mx-auto"
        style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
      >
        {cards.map((card, idx) => {
          const isFlipped = flipped.includes(idx) || matched.includes(idx) || peeking;
          return (
            <button
              key={`card-${idx}-${card.symbol}`}
              onClick={() => handleCardClick(idx)}
              className={`
                aspect-square rounded-xl text-xl font-bold transition-all duration-300 transform
                ${isFlipped
                  ? `${card.color} text-white scale-100`
                  : "bg-sky-200 hover:bg-sky-300 text-transparent scale-95 hover:scale-100"
                }
                ${matched.includes(idx) ? "opacity-50 scale-90" : ""}
              `}
              disabled={!canFlip || matched.includes(idx)}
              data-testid={`card-${idx}`}
            >
              {isFlipped ? card.symbol : "?"}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PatternMatchGame;
